import { ConfirmationDialog } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useSyncExternalStore } from "react";

export type ConfirmDialogOptions = {
  title: string;
  message: string;
  /** Verb of the action (« Supprimer », « Valider »), never « OK ». */
  confirmLabel: string;
  /** Defaults to « Annuler ». */
  cancelLabel?: string;
};

type DialogState = {
  open: boolean;
  options: ConfirmDialogOptions;
  resolve: (confirmed: boolean) => void;
};

// Module-level store, like `toaster`: any code can ask, the single
// <ConfirmDialogHost /> mounted in App renders the popup.
let state: DialogState | null = null;
const listeners = new Set<() => void>();

function setState(next: DialogState | null) {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      state?.resolve(false);
      state = null;
    }
  };
}

/**
 * Asks for confirmation in the application popup (never `window.confirm`).
 * Resolves `true` when the user clicks the action verb, `false` on
 * « Annuler ».
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  if (listeners.size === 0) {
    console.error("confirmDialog called without a mounted ConfirmDialogHost");
    return Promise.resolve(false);
  }
  // A second request (double click) answers the first one with « Annuler ».
  state?.resolve(false);
  return new Promise((resolve) => setState({ open: true, options, resolve }));
}

/** Renders `confirmDialog` requests. Mount once, in App. */
export function ConfirmDialogHost() {
  const current = useSyncExternalStore(subscribe, () => state);
  if (isNil(current)) return null;

  // Closing keeps the options so the exit animation shows the same text.
  const answer = (confirmed: boolean) => {
    current.resolve(confirmed);
    setState({ ...current, open: false });
  };

  return (
    <ConfirmationDialog
      open={current.open}
      title={current.options.title}
      confirmLabel={current.options.confirmLabel}
      cancelLabel={current.options.cancelLabel}
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    >
      {current.options.message}
    </ConfirmationDialog>
  );
}
